import React from 'react';
import Plot from 'react-plotly.js';
import Grid from '@mui/material/Grid';

const plotLayout = (title, yMax = null) => ({
  title: {
    text: title,
    font: { color: 'white', size: 16 },
    x: 0.5,
    xanchor: 'center'
  },
  margin: { l: 40, r: 20, t: 30, b: 50 },
  paper_bgcolor: 'rgba(0,0,0,0)',
  plot_bgcolor: 'rgba(0,0,0,0)',
  font: { color: 'white' },
  xaxis: {
    color: 'white',
    tickfont: { color: 'white' },
    tickangle: -45,
    automargin: true
  },
  yaxis: {
    color: 'white',
    tickfont: { color: 'white' },
    automargin: true,
    ...(yMax !== null ? { range: [0, yMax * 1.2] } : {})
  },
  legend: {
    orientation: 'h',
    y: -0.2,
    x: 0.5,
    xanchor: 'center'
  }
});

const simulateBoxData = (stat) => {
  const data = [];
  const safePush = (val, times = 3) => {
    if (val != null) for (let i = 0; i < times; i++) data.push(val);
  };
  safePush(stat.min, 1);
  safePush(stat.q1, 3);
  safePush(stat.median, 3);
  safePush(stat.q3, 3);
  safePush(stat.max, 1);
  return data;
};

const extractBoxPlotData = (stats, titlePrefix) => {
  const plots = [];
  if (!stats || typeof stats !== 'object') return plots;

  Object.entries(stats).forEach(([scenario, value]) => {
    if (!value || typeof value !== 'object') return;

    const isNested = Object.values(value).some(
      v => v && typeof v === 'object' && 'q1' in v
    );

    if (isNested) {
      const boxTraces = [];
      const outlierTraces = [];
      let globalMax = 0;

      Object.entries(value).forEach(([type, val]) => {
        if (!val || val.q1 == null || val.median == null || val.q3 == null) return;

        const label = type.toUpperCase();
        const simulatedData = simulateBoxData(val);
        globalMax = Math.max(globalMax, val.max || 0, ...(val.outliers || []));

        boxTraces.push({
          x: Array(simulatedData.length).fill(label),
          y: simulatedData,
          type: 'box',
          name: label,
          boxpoints: false,
          marker: { color: 'blue' },
          line: { color: 'blue' }
        });

        if (val.outliers && val.outliers.length > 0) {
          outlierTraces.push({
            x: Array(val.outliers.length).fill(label),
            y: val.outliers,
            type: 'scatter',
            mode: 'markers',
            name: `${label} Outliers`,
            marker: { symbol: 'circle', size: 5, color: 'orange' },
            hoverinfo: 'y'
          });
        }
      });

      plots.push(
        <Grid item lg={9} md={9} xs={12} key={titlePrefix + scenario}>
          <Plot
            data={[...boxTraces, ...outlierTraces]}
            layout={plotLayout(`${titlePrefix} - ${scenario}`, globalMax)}
            useResizeHandler
            style={{ width: '100%', height: '350px' }}
            config={{ responsive: true }}
          />
        </Grid>
      );
    } else {
      if (value.q1 == null || value.median == null || value.q3 == null) return;

      const label = scenario;
      const simulatedData = simulateBoxData(value);
      const yMax = Math.max(value.max || 0, ...(value.outliers || []));

      const box = {
        x: Array(simulatedData.length).fill(label),
        y: simulatedData,
        type: 'box',
        name: label,
        boxpoints: false,
        marker: { color: 'blue' },
        line: { color: 'blue' }
      };

      const outliers = {
        x: Array((value.outliers?.length || 0)).fill(label),
        y: value.outliers || [],
        type: 'scatter',
        mode: 'markers',
        name: `${label} Outliers`,
        marker: { symbol: 'circle', size: 5, color: 'orange' },
        hoverinfo: 'y'
      };

      plots.push(
        <Grid item lg={3} md={6} xs={12} key={titlePrefix + label}>
          <Plot
            data={[box, outliers]}
            layout={plotLayout(`${titlePrefix} - ${label}`, yMax)}
            useResizeHandler
            style={{ width: '100%', height: '350px' }}
            config={{ responsive: true }}
          />
        </Grid>
      );
    }
  });

  return plots;
};

const ChildChart = ({ data }) => {
  const charts = [];

  const mapBlock = [
    ['meanTravelTimePerVehicle', 'meanTravelTime', 'Mean Travel Time Per Vehicle'],
    ['meanTravelTimePerVehicleType', 'meanTravelTimePerType', 'Mean Travel Time Per Vehicle Type'],
    ['waitingTimePerVehicle', 'waitingTime', 'Waiting Time Per Vehicle'],
    ['waitingTimePerVehicleType', 'waitingTime', 'Waiting Time Per Vehicle Type'],
    ['waitingCountPerVehicle', 'waitingCount', 'Waiting Count Per Vehicle'],
    ['waitingCountPerVehicleType', 'waitingCount', 'Waiting Count Per Vehicle Type'],
    ['speedAvgPerVehicle', 'speedAvg', 'Average Speed Per Vehicle'],
    ['speedAvgPerVehicleType', 'speedAvg', 'Average Speed Per Vehicle Type']
  ];

  mapBlock.forEach(([key1, key2, title]) => {
    if (data[key1]?.[key2]) {
      charts.push(...extractBoxPlotData(data[key1][key2], title));
    }
  });

  if (data.throughput?.throughput) {
    Object.entries(data.throughput.throughput).forEach(([scenario, value]) => {
      if (typeof value !== 'number') return;
      charts.push(
        <Grid item lg={3} md={6} xs={12} key={`throughput-${scenario}`}>
          <Plot
            data={[
              {
                x: [scenario],
                y: [value],
                type: 'bar',
                name: 'Throughput',
                marker: { color: 'orange' }
              }
            ]}
            layout={plotLayout(`Throughput - ${scenario}`, value*1.2)}
            useResizeHandler
            style={{ width: '100%', height: '350px' }}
            config={{ responsive: true }}
          />
        </Grid>
      );
    });
  }

  return charts;
};

export default ChildChart;
